<template>
  <div class="home">
    <div class="hero-section">
      <h1 class="hero-title">
        <span class="hero-line">Plan a time that works for everyone ⏱️</span>
      </h1>
      <p class="hero-subtitle">
        Create the link, send it around, and see when your crew can meet.
      </p>
    </div>

    <div class="form-container">
      <form @submit.prevent="submitHandler" class="event-form">
        <div class="form-group full-width">
          <label for="event-name" class="form-label"
            >Event Name (optional)</label
          >
          <input
            id="event-name"
            type="text"
            v-model="name"
            placeholder="Leave blank for auto-generated name"
            class="form-input"
          />
        </div>

        <div class="form-group full-width">
          <label class="form-label"
            >Select dates (drag to select multiple)</label
          >
          <div class="date-calendar">
            <div class="calendar-header">
              <button type="button" @click="previousMonth" class="nav-btn">
                &lt;
              </button>
              <span class="month-label">{{ currentMonthLabel }}</span>
              <button type="button" @click="nextMonth" class="nav-btn">
                &gt;
              </button>
            </div>
            <div class="calendar-grid">
              <div
                class="day-header"
                v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
                :key="day"
              >
                {{ day }}
              </div>
              <div
                v-for="(date, idx) in calendarDates"
                :key="idx"
                class="calendar-day"
                :class="{
                  empty: !date,
                  selected: date && selectedDates.includes(date),
                  'other-month': date && !isSameMonth(date),
                }"
                @mousedown="startDateDrag(date)"
                @mouseenter="dragDate(date)"
                @mouseup="endDateDrag"
              >
                {{ date ? new Date(date).getDate() : "" }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="stime" class="form-label"
              >Available between (start time)</label
            >
            <input
              type="time"
              id="stime"
              v-model="stime"
              class="form-input"
              required
            />
          </div>

          <div class="form-group">
            <label for="etime" class="form-label"
              >Available between (end time)</label
            >
            <input
              type="time"
              id="etime"
              v-model="etime"
              class="form-input"
              required
            />
          </div>
        </div>

        <button type="submit" class="btn btn-primary btn-submit">
          Create Event & Get Link
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { instance } from "../api";

// Auto-generate event names with 4-character codes
const generateEventName = () => {
  // Generate 4-character code with mix of letters and numbers
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";
  for (let i = 0; i < 4; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  // 50% chance for "Meeting", 50% chance for "Event"
  const prefix = Math.random() < 0.5 ? "Meeting" : "Event";

  return `${prefix} #${code}`;
};

export default {
  name: "Home",
  data() {
    return {
      name: "",
      sdate: "",
      edate: "",
      stime: "09:00",
      etime: "17:00",
      error: {
        sdate: "",
        edate: "",
      },
      currentMonth: new Date(),
      selectedDates: [],
      isDragging: false,
      dragAction: "add", // 'add' or 'remove'
    };
  },
  computed: {
    currentMonthLabel() {
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${
        months[this.currentMonth.getMonth()]
      } ${this.currentMonth.getFullYear()}`;
    },
    calendarDates() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      // First day of the month
      const firstDay = new Date(year, month, 1);
      const firstDayOfWeek = firstDay.getDay();

      // Last day of the month
      const lastDay = new Date(year, month + 1, 0);
      const lastDate = lastDay.getDate();

      // Previous month's trailing dates
      const prevMonthLastDay = new Date(year, month, 0).getDate();

      const dates = [];

      // Add previous month's dates
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevMonthLastDay - i);
        dates.push(this.formatDate(date));
      }

      // Add current month's dates
      for (let i = 1; i <= lastDate; i++) {
        const date = new Date(year, month, i);
        dates.push(this.formatDate(date));
      }

      // Add next month's dates to complete the grid
      const remainingDays = 42 - dates.length; // 6 rows * 7 days
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        dates.push(this.formatDate(date));
      }

      return dates;
    },
  },
  mounted() {
    // Set default dates - select next 5 days
    const today = new Date();
    this.selectedDates = [];

    for (let i = 0; i <= 5; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      this.selectedDates.push(this.formatDate(date));
    }

    // Update sdate and edate based on selected dates
    this.updateDateRange();

    // Add mouseup listener to document to handle drag end
    document.addEventListener("mouseup", this.endDateDrag);
  },
  beforeUnmount() {
    document.removeEventListener("mouseup", this.endDateDrag);
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    isSameMonth(dateStr) {
      const date = new Date(dateStr);
      return (
        date.getMonth() === this.currentMonth.getMonth() &&
        date.getFullYear() === this.currentMonth.getFullYear()
      );
    },
    previousMonth() {
      this.currentMonth = new Date(
        this.currentMonth.getFullYear(),
        this.currentMonth.getMonth() - 1,
        1
      );
    },
    nextMonth() {
      this.currentMonth = new Date(
        this.currentMonth.getFullYear(),
        this.currentMonth.getMonth() + 1,
        1
      );
    },
    startDateDrag(date) {
      if (!date) return;

      this.isDragging = true;

      // Determine if we're adding or removing based on current state
      if (this.selectedDates.includes(date)) {
        this.dragAction = "remove";
        this.selectedDates = this.selectedDates.filter((d) => d !== date);
      } else {
        this.dragAction = "add";
        this.selectedDates.push(date);
      }

      this.updateDateRange();
    },
    dragDate(date) {
      if (!this.isDragging || !date) return;

      if (this.dragAction === "add") {
        if (!this.selectedDates.includes(date)) {
          this.selectedDates.push(date);
        }
      } else {
        this.selectedDates = this.selectedDates.filter((d) => d !== date);
      }

      this.updateDateRange();
    },
    endDateDrag() {
      this.isDragging = false;
    },
    updateDateRange() {
      if (this.selectedDates.length === 0) {
        this.sdate = "";
        this.edate = "";
        return;
      }

      // Sort dates and get min/max
      const sortedDates = [...this.selectedDates].sort();
      this.sdate = sortedDates[0];
      this.edate = sortedDates[sortedDates.length - 1];
    },
    async submitHandler() {
      // Validate dates
      this.error = { sdate: "", edate: "" };

      if (this.selectedDates.length === 0) {
        this.error.sdate = "Please select at least one date";
        return;
      }

      const startDate = new Date(this.sdate).getTime();
      const endDate = new Date(this.edate).getTime();

      if (startDate > endDate) {
        this.error.edate = "End date must be after start date";
        return;
      }

      // Use auto-generated name if none provided
      const eventName = this.name.trim() || generateEventName();

      const eventDetails = {
        event_name: eventName,
        start_date: this.sdate,
        end_date: this.edate,
        start_time: this.stime,
        end_time: this.etime,
      };

      try {
        const response = await instance.post("/add", eventDetails);
        const eventID = response.data;

        // Redirect to event page
        this.$router.push({ name: "event", params: { id: eventID } });
      } catch (error) {
        console.error("Error creating event:", error);
        alert("Failed to create event. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
.home {
  max-width: 600px;
  margin: 0 auto;
  padding: 3rem 1rem;
}

.hero-section {
  text-align: center;
  margin-bottom: 3rem;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 500;
  line-height: 1.2;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;
}

.hero-line {
  display: block;
}

.hero-line + .hero-line {
  margin-top: 0.25rem;
}

.hero-subtitle {
  font-size: 1.125rem;
  color: #737373;
  line-height: 1.6;
}

.form-container {
  background: #ffffff;
  border: 1px solid #f5f5f5;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.event-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.full-width {
  grid-column: 1 / -1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #404040;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  font-size: 0.95rem;
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  transition: all 0.2s ease;
  color: #1a1a1a;
}

.form-input::placeholder {
  color: #a3a3a3;
}

.form-input:hover {
  border-color: #d4d4d4;
}

.form-input:focus {
  background: #ffffff;
  border-color: #a3a3a3;
  outline: none;
}

.input-error {
  border-color: #dc2626 !important;
}

.form-error {
  font-size: 0.75rem;
  color: #dc2626;
  margin-top: -0.25rem;
}

.btn-submit {
  margin-top: 0.5rem;
  padding: 0.875rem;
  font-size: 1rem;
  width: 100%;
}

/* Calendar Styles */
.date-calendar {
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 0.75rem;
  user-select: none;
  width: 100%;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e5e5;
}

.month-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a1a;
}

.nav-btn {
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  color: #404040;
  padding: 0;
}

.nav-btn:hover {
  background: #f5f5f5;
  border-color: #d4d4d4;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
}

.day-header {
  text-align: center;
  font-size: 0.65rem;
  font-weight: 500;
  color: #737373;
  padding: 0.375rem 0;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.75rem;
  color: #404040;
  font-weight: 400;
  min-height: 32px;
}

.calendar-day:hover:not(.empty):not(.other-month) {
  background: #f5f5f5;
  border-color: #d4d4d4;
}

.calendar-day.selected {
  background: #1a1a1a;
  border-color: #1a1a1a;
  color: #ffffff;
  font-weight: 500;
}

.calendar-day.other-month {
  color: #d4d4d4;
  background: #fafafa;
  cursor: default;
}

.calendar-day.empty {
  background: transparent;
  border: none;
  cursor: default;
}

@media (max-width: 768px) {
  .home {
    padding: 2rem 1rem;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .form-container {
    padding: 1.5rem;
    border-radius: 12px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}
</style>
